import Constants from '../helpers/constants';
import FetchHelper from '../helpers/fetch_helper';
import WindowHelper from '../helpers/window_helper';
import SleepHelper from '../helpers/sleep_helper';
import Log from '../helpers/log';
import Actions from '../actions/actions';
import StepsHelper from '../helpers/financing_request/steps_helper';
import AuthHelper from '../helpers/auth_helper';

const FinancingRequestDispatcher = {
  //defaults to current stepSku
  async cancelStepDocEnvelope({ custId, stepSku }) {
    let promise;
    try {
      let customerId = custId ?? this.getStoreVal('customer').id;
      let currentStepSku =
        stepSku ?? StepsHelper.findCurrentUnapprovedStepExternalSku(this.getStoreVal('financingRequest'));
      let url =
        currentStepSku === 'CS'
          ? `customers/${customerId}/steps/${currentStepSku}/docs/signatures`
          : `customers/${customerId}/steps/${currentStepSku}/request-funding`;
      promise = await FetchHelper.fetchJson(url, { method: 'DELETE', version: '/v3' });
      if (promise.code == null) {
        //means we got a 403
        Actions.setErrorMessages('You no longer have permission to cancel documents');
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not cancel signatures' },
      });
    }
    return promise;
  },

  //defaults to current stepSku
  async resendStepDocEnvelope({ custId, stepSku }) {
    try {
      let customerId = custId ?? this.getStoreVal('customer').id;
      let currentStepSku =
        stepSku ?? StepsHelper.findCurrentUnapprovedStepExternalSku(this.getStoreVal('financingRequest'));
      const url = `customers/${customerId}/steps/${currentStepSku}/docs/signatures/resend`;
      const response = await FetchHelper.fetchJson(url, { method: 'POST', version: '/v3' });
      if (response.code == null) {
        //means we got a 403
        Actions.setErrorMessages('You no longer have permission to send documents');
      } else {
        Actions.setMessages('Documents resent for signature');
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not resend documents for signature' },
      });
    }
  },

  async createFinancingRequest() {
    let promise;
    try {
      let customer = this.getStoreVal('customer');
      const url = `customers/${customer.id}/financing-requests`;
      promise = await FetchHelper.fetchJson(url, { method: 'POST', version: '/v3' });
      // financing method just got created, refresh financing methods so the store knows which one isPrimary
      await this.dispatch({ type: 'refreshFinancingMethods' });
      this.dispatch({ type: 'fetchCustomer', data: this.getStoreVal('customer')?.id });
      return promise;
    } catch (error) {
      if (error.response.status !== 422) {
        this.dispatch({
          type: 'handleRequestError',
          data: { error, defaultErrorMessage: 'Could not create financing request' },
        });
      }
      return error.response;
    }
  },

  async deleteFinancingRequest({
    customerId,
    afterHandoff = false,
    doNotCancelCustomer = false,
    doNotNotifyEnsemble = false,
    force = false,
    cancellationReason,
  }) {
    this.dispatch({ type: 'endPolling', data: 'fetchFinancingRequest' });
    try {
      await FetchHelper.fetchJson(`customers/${customerId}`, {
        method: 'DELETE',
        version: '/v3',
        body: {
          cancellationReason,
          doNotNotifyEnsemble,
          doNotCancelCustomer,
        },
      });
      this.setStoreVal('financingRequest', null);
      await Actions.fetchCustomerBannerData({ customerId, status: 'cancelled' });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not delete financing request' },
      });
      return Promise.reject('Could not delete financing request');
    }
  },

  async fetchFinancingRequest({ customerId, waitOnGeneratedDocs }) {
    if (customerId == null) customerId = this.getStoreVal('customer')?.id;
    if (!customerId) {
      console.error('No customerID passed in or in store for fetchFinancingRequest!');
      return;
    }

    let promise;
    let waitingDocs;
    let financingRequest;

    let retryDelays = [0];
    if (waitOnGeneratedDocs) {
      // delay 1000ms, then 5000s, if requests doesn't return fully generated docs, and the caller specified to waitOnGeneratedDocs
      retryDelays.push(1000, 5000);
    }

    for (let i = 0; i < retryDelays.length; i++) {
      if (retryDelays[i] > 0) {
        Log.trace(`Sleeping ${retryDelays[i]}ms before retrying financing request`);
        await SleepHelper.sleep(retryDelays[i]);
      }

      this.incrementLock('fetchFinancingRequest');
      try {
        promise = await FetchHelper.fetchJson(`customers/${customerId}/financing-requests`, {
          method: 'GET',
          version: '/v3',
        });
        financingRequest = promise.results[0];
        this.setStoreValWithCheck('financingRequest', financingRequest);
      } catch (error) {
        if (window.location.href.includes('/step-documents') && error?.body?.code === 302) {
          window.location.href = error?.messages?.[0];
        } else {
          this.dispatch({
            type: 'handleRequestError',
            data: { error, defaultErrorMessage: 'Could not retrieve financing request' },
          });
        }
      } finally {
        this.decrementLock('fetchFinancingRequest');
      }

      if (waitOnGeneratedDocs) {
        if (financingRequest.steps && financingRequest.steps[0].docs) {
          waitingDocs = financingRequest.steps[0].docs.filter(
            ({ formId, independentlyRegenerateableFormId, currentRevision, dataEntryElementSetId }) =>
              formId !== null &&
              independentlyRegenerateableFormId === null &&
              dataEntryElementSetId === null &&
              !(currentRevision && currentRevision.creationMethod)
          );
          if (waitingDocs.length === 0) {
            break;
          } else {
            Log.trace(`Generated docs did not get properly returned for customerId ${customerId}`);
          }
        }
      }
    }

    if (
      waitOnGeneratedDocs &&
      (!(financingRequest.steps && financingRequest.steps[0].docs) || waitingDocs.length > 0)
    ) {
      Log.error(`Generated docs did not get properly returned for customerId ${customerId}, even after two retries`);
    }

    return promise;
  },

  async fetchWorkflowProofOverrides({ customerId }) {
    let workflowParams;
    try {
      const promise = await FetchHelper.fetchJson(
        `customers/${customerId}/financing-requests/workflow-events?stepDocSku=SystemMonitoringType&eventActionId=26`,
        { version: '/v3' }
      );

      workflowParams = JSON.parse(promise?.results?.[0]?.parameters);
    } catch (e) {
      workflowParams = [];
    }
    this.setStoreValWithCheck('workflowProofOverrides', workflowParams);

    return workflowParams;
  },

  async submitStepDocsForReview({ customerId, stepDocSkus }) {
    try {
      return (
        await FetchHelper.fetchJson(`customers/${customerId}/financing-requests/steps/docs/review`, {
          version: '/v3',
          method: 'POST',
          body: { stepDocSkus },
        })
      )?.result;
    } catch (errors) {
      Actions.setErrorMessages(errors.messages);
    }
  },

  async uploadStepDocRevision({
    uploadFile,
    financingRequestId,
    stepId,
    docId,
    currentRevisionId,
    onUploadProgress,
    onUploadError,
    onUploadEnd,
    revokeReview = true,
  }) {
    let response;
    this.incrementLock('uploadFile');
    try {
      let queryparams = '';
      if (currentRevisionId) {
        queryparams = `?currentRevisionId=${currentRevisionId}`;
      }
      if (revokeReview === false) {
        if (queryparams) {
          queryparams = `${queryparams}&revokeReview=false`;
        } else {
          queryparams = `?revokeReview=false`;
        }
      }

      response = await FetchHelper.uploadFile({
        path: `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/revisions${queryparams}`,
        uploadFile,
        onUploadProgress: onUploadProgress,
        onUploadError: onUploadError,
        onUploadEnd: onUploadEnd,
        method: 'POST',
      });
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Could not upload file' } });
    } finally {
      this.decrementLock('uploadFile');
    }

    return response;
  },

  async uploadStepDocCustomerRevision({
    uploadFile,
    financingRequestId,
    stepId,
    docId,
    onUploadProgress,
    onUploadError,
    onUploadEnd,
  }) {
    let response;
    this.incrementLock('uploadFile');
    try {
      response = await FetchHelper.uploadFile({
        path: `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/customer/revisions`,
        uploadFile,
        onUploadProgress: onUploadProgress,
        onUploadError: onUploadError,
        onUploadEnd: onUploadEnd,
        method: 'POST',
      });
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Could not upload file' } });
    } finally {
      this.decrementLock('uploadFile');
    }

    return response;
  },

  async signStepDocs(stepDocsSignRequest) {
    let response;
    try {
      response = await FetchHelper.generate({
        path: 'financing-requests/steps/docs/sign',
        method: 'POST',
        body: stepDocsSignRequest,
      });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Failed to sign financing documents' },
      });
    }
    return response;
  },

  async deleteStepDocRevision({ financingRequestId, stepId, docId }) {
    let response;
    this.incrementLock('uploadFile');
    try {
      response = await FetchHelper.fetchJson(
        `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/revisions`,
        { method: 'DELETE' }
      );
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Could not delete file' } });
    } finally {
      this.decrementLock('uploadFile');
    }

    return response;
  },

  async fetchHomeownerPacketRevisionBytes({ customerId, stepName }) {
    try {
      const bytesPromise = FetchHelper.fetchJson(
        `customers/${customerId}/steps/${stepName}/docs/homeowner-packet/bytes`,
        {
          method: 'GET',
          version: '/v3',
          isBytes: true,
          headers: {
            authorization: `Bearer ${
              sessionStorage.getItem('WC_JWT_Token')
                ? sessionStorage.getItem('WC_JWT_Token')
                : AuthHelper.getSessionToken()
            }`,
          },
        }
      );
      WindowHelper.openFile(bytesPromise);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not retrieve bytes for Packet' },
      });
    }
  },

  async fetchStepDocRevisionBytes({ financingRequestId, stepId, docId, revisionId, includeDeleted }) {
    try {
      const bytesPromise = FetchHelper.fetchJson(
        `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/revisions/${revisionId}/bytes${
          includeDeleted ? '?includeDeleted=true' : ''
        }`,
        {
          method: 'GET',
          isBytes: true,
        }
      );
      WindowHelper.openFile(bytesPromise);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not retrieve financing request step doc revision' },
      });
    }
  },

  async openStepDocRevisionBytes({ financingRequestId, stepId, docId, revisionId, filename }) {
    try {
      const bytes = await FetchHelper.fetchJson(
        `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/revisions/${revisionId}/bytes`,
        {
          method: 'GET',
          isBytes: true,
        }
      );
      WindowHelper.goToFile(bytes, filename);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not retrieve financing request step doc revision' },
      });
    }
  },

  async sendDataEntry({ customerId, stepSku, docSku, dataEntry, onGenerateProgress, onGenerateError, onGenerateEnd }) {
    const results = await FetchHelper.generate({
      path: `customers/${customerId}/steps/${stepSku}/docs/${docSku}/data-entry`,
      method: 'POST',
      version: '/v3',
      body: dataEntry,
      onGenerateProgress,
      onGenerateError,
      onGenerateEnd,
    });
    if (docSku === 'SystemMonitoringType') {
      await Actions.fetchCustomerInverterMonitoring({ customerId });
    }
    return results;
  },

  async generateDataEntryStepDocRevision(data) {
    let currentRevision;
    try {
      const response = await this.dispatch({ type: 'sendDataEntry', data });
      currentRevision = response.results?.[0];
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not generate data entry step doc revision' },
      });
    }
    return currentRevision;
  },

  async sendPTODataEntry(data) {
    try {
      const response = await this.dispatch({
        type: 'sendDataEntry',
        data: { ...data, stepSku: 'PTO', docSku: 'PTOAttestation' },
      });

      let responseLevel = Math.floor((response.code / 100) % 10);
      if (responseLevel === 2) {
        Actions.setMessages('PTO Complete');
      } else if (responseLevel === 4) {
        Actions.setErrorMessages('This customer is no longer on the PTO step');
      } else {
        Actions.setErrorMessages('Could not generate data entry');
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not generate data entry' },
      });
    }
  },

  async generateIRStepDocRevision({
    financingRequestId,
    stepId,
    stepDocId,
    onGenerateProgress,
    onGenerateError,
    onGenerateEnd,
  }) {
    let currentRevision;
    this.incrementLock('irDocGeneration');
    try {
      const response = await FetchHelper.generate({
        path: `financing-requests/${financingRequestId}/steps/${stepId}/docs/revisions/ir-documents`,
        onGenerateProgress: onGenerateProgress,
        onGenerateError: onGenerateError,
        onGenerateEnd: onGenerateEnd,
        method: 'POST',
        body: {
          stepDocsIds: Array.isArray(stepDocId) ? stepDocId : [stepDocId],
        },
      });

      currentRevision = response.results[0];
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not generate IR step doc revision' },
      });
    }
    this.decrementLock('irDocGeneration');
    return currentRevision;
  },

  async getDiscussions({ financingRequestId, stepId, docId }) {
    let promise;
    try {
      const url = `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/discussions`;
      promise = await FetchHelper.fetchJson(url, { method: 'GET' });
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Could not get discussions' } });
    }
    return promise;
  },

  async postDiscussion({ financingRequestId, stepId, docId, message }) {
    let promise;
    try {
      const url = `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/discussions`;
      promise = await FetchHelper.fetchJson(url, {
        method: 'POST',
        body: {
          message,
        },
      });
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Could not post discussion' } });
    }
    return promise;
  },

  async sendStepDocsForSignatureByEmail({ stepSku, body }) {
    let customerId = this.getStoreVal('customer').id;
    const url =
      stepSku === 'CS'
        ? `customers/${customerId}/steps/${stepSku}/docs/signatures/by-email`
        : `customers/${customerId}/steps/${stepSku}/request-funding`;
    try {
      const response = await FetchHelper.fetchJson(url, { method: 'POST', version: '/v3', body });
      if (response.status === 'success') {
        await this.dispatch({ type: 'fetchFinancingRequest', data: { customerId } });
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not send documents for signature via email' },
      });
    }
  },

  async fetchMeasures({ productType }) {
    let promise;
    let measures;
    try {
      if (productType === Constants.PRODUCT_TYPE_PACE || productType === Constants.PRODUCT_TYPE_LOAN) {
        measures = Constants.SOLAR_MEASURES;
      } else {
        const url = 'measures';
        promise = await FetchHelper.fetchJson(url, { method: 'GET' });
        measures = promise.results;
      }
      this.setStoreVal('measures', measures);
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Could not get measures' } });
    }
    return promise;
  },

  initiateStepDocsInPersonSigning({ financingRequestId, stepId, docsToSign }) {
    try {
      const url = `financing-requests/${financingRequestId}/steps/${stepId}/docs/signatures/in-person`;
      return FetchHelper.fetchJson(url, { method: 'POST', body: { docs: docsToSign } }).then(({ results }) => {
        if (results.length === 1) {
          WindowHelper.goTo(results[0]);
        } else {
          results.forEach((url) => {
            WindowHelper.open(url);
          });
        }
      });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not initiate In-Person signing' },
      });
      return null;
    }
  },
};

export default FinancingRequestDispatcher;
